import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { query, orderBy, getDocs } from "firebase/firestore";
import { format } from "date-fns";
import { propertyCollection } from "../../utils/firestorePaths";
import { drawReportHeader, stampPageNumbers } from "../../utils/reports/pdfChrome";
import { downloadBlob } from "../../utils/reports/downloadBlob";
import { buildReportFilename } from "../../utils/reports/reportFilename";

const loadImage = (url) =>
  new Promise(async (resolve, reject) => {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => resolve({ img, objUrl });
      img.onerror = () => reject(new Error("Image decode failed"));
      img.src = objUrl;
    } catch (err) {
      reject(err);
    }
  });

const fmtUSD = (n) =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(
    Number.isFinite(Number(n)) ? Number(n) : 0
  );

const GenerateRoomStatusReport = async ({ room, clientId, selectedProperty, items, user }) => {
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const marginX = 12;

  const now = new Date();
  const reportDate = format(now, "MMMM d, yyyy");
  const reportTime = format(now, "h:mm a");
  const generatedBy = user?.fullName || user?.personalEmail || "Unknown";

  // ── Build items needed list ──────────────────────────────────────────
  const itemsNeeded = [];
  const currencyVal = (n) => {
    const v = Number.parseFloat(n ?? 0);
    return Number.isFinite(v) ? v : 0;
  };

  Object.keys(room).forEach((key) => {
    if (!key.startsWith("itemCount")) return;
    const qty = Number(room[key]);
    if (qty <= 0) return;
    const matched = (items ?? []).find((it) => it.itemID === key);
    if (!matched) return;
    itemsNeeded.push({
      vendorName: (matched.vendor || "Unassigned Vendor").trim() || "Unassigned Vendor",
      itemName: matched.itemName || key,
      unitCost: currencyVal(matched.itemCost),
      qty,
      lineTotal: currencyVal(matched.itemCost) * qty,
    });
  });

  const grandTotal = itemsNeeded.reduce((s, r) => s + r.lineTotal, 0);

  // ── Fetch photos early so we know total page count ───────────────────
  let photosForDate = [];
  let photoDateLabel = "";

  try {
    const imagesRef = propertyCollection(
      clientId,
      selectedProperty,
      "units",
      room.displayName,
      "images"
    );
    const snap = await getDocs(query(imagesRef, orderBy("uploadedAt", "desc")));
    const allImages = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

    if (allImages.length > 0) {
      const mostRecentTs = allImages[0].uploadedAt;
      const mostRecentDate = mostRecentTs?.toDate ? mostRecentTs.toDate() : new Date(mostRecentTs);
      const mostRecentDateStr = format(mostRecentDate, "yyyy-MM-dd");
      photoDateLabel = format(mostRecentDate, "MMMM d, yyyy");

      photosForDate = allImages.filter((img) => {
        const ts = img.uploadedAt;
        const d = ts?.toDate ? ts.toDate() : new Date(ts);
        return format(d, "yyyy-MM-dd") === mostRecentDateStr;
      });
    }
  } catch (err) {
    console.error("Error fetching images for report:", err);
  }

  const perPage = 9;
  // ── Page 1: Cover / Status Summary ─────────────────────────────────
  // Standardized header
  const contentTop = drawReportHeader(pdf, {
    title: "Room Status Report",
    generatedBy,
  });

  // Room number
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(32);
  pdf.text(`Room ${room.displayName}`, marginX, contentTop + 14);

  // Meta grid
  const metaStartY = contentTop + 26;
  const metaLineH = 7;
  const labelX = marginX;
  const valueX = 60;

  const metaRows = [
    ["Report Date:", reportDate],
    ["Status:", room.status ?? "—"],
    ["Housekeeping:", room.selectedHousekeepingRequirements ?? "—"],
    ["Maintenance:", room.selectedMaintenanceRequirements ?? "—"],
    ["Generated by:", generatedBy],
    ["Generated at:", `${reportDate} ${reportTime}`],
  ];

  pdf.setFontSize(10);
  metaRows.forEach(([label, value], i) => {
    const y = metaStartY + i * metaLineH;
    pdf.setFont("helvetica", "bold");
    pdf.text(label, labelX, y);
    pdf.setFont("helvetica", "normal");
    pdf.text(String(value), valueX, y);
  });

  // Cost chip
  const costY = metaStartY + metaRows.length * metaLineH + 8;
  pdf.setFillColor(25, 118, 210);
  pdf.roundedRect(marginX, costY - 5, 120, 9, 2, 2, "F");
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9);
  pdf.setTextColor(255, 255, 255);
  pdf.text(
    `Estimated Cost to Bring Up: ${fmtUSD(grandTotal)}`,
    marginX + 3,
    costY + 1
  );
  pdf.setTextColor(0);

  // Divider + items teaser
  const dividerY = costY + 14;
  pdf.setDrawColor(200);
  pdf.line(marginX, dividerY, pageWidth - marginX, dividerY);

  if (itemsNeeded.length > 0) {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.text("Items Needed:", marginX, dividerY + 7);
    pdf.setFont("helvetica", "normal");
    const teaser = itemsNeeded.map((r) => `${r.itemName} ×${r.qty}`).join(", ");
    const wrapped = pdf.splitTextToSize(teaser, pageWidth - marginX * 2);
    pdf.text(wrapped, marginX, dividerY + 14);
  } else {
    pdf.setFont("helvetica", "italic");
    pdf.setFontSize(10);
    pdf.text("No items needed.", marginX, dividerY + 7);
  }

  // ── Photo pages (3×3 grid) ──────────────────────────────────────────
  if (photosForDate.length > 0) {
    const pages = Math.ceil(photosForDate.length / perPage);

    for (let pageIdx = 0; pageIdx < pages; pageIdx++) {
      pdf.addPage();

      // Page header
      pdf.setFillColor(40, 40, 40);
      pdf.rect(0, 0, pageWidth, 14, "F");
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10);
      pdf.setTextColor(255, 255, 255);
      pdf.text(`Room ${room.displayName} — Photos (${photoDateLabel})`, marginX, 9);
      pdf.setTextColor(0);

      const batch = photosForDate.slice(pageIdx * perPage, (pageIdx + 1) * perPage);

      // Load all images in batch
      const loaders = batch.map((imgDoc, idx) =>
        loadImage(imgDoc.url)
          .then(({ img, objUrl }) => ({ status: "ok", idx, img, objUrl, url: imgDoc.url }))
          .catch(() => ({ status: "fail", idx, url: imgDoc.url }))
      );
      const results = await Promise.all(loaders);

      // 3×3 grid
      const startX = marginX;
      const startY = 20;
      const tileW = 57;
      const tileH = 57;
      const gapX = 62;
      const gapY = 65;
      const perRow = 3;

      results.forEach((res, i) => {
        const col = i % perRow;
        const row2 = Math.floor(i / perRow);
        const x = startX + col * gapX;
        const y = startY + row2 * gapY;

        if (res.status === "ok" && res.img) {
          try {
            pdf.addImage(res.img, "JPEG", x, y, tileW, tileH);
            pdf.link(x, y, tileW, tileH, { url: res.url });
          } catch {
            pdf.setFillColor(220, 220, 220);
            pdf.rect(x, y, tileW, tileH, "F");
            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(7);
            pdf.text("Image unavailable", x + 2, y + tileH / 2);
          } finally {
            if (res.objUrl) URL.revokeObjectURL(res.objUrl);
          }
        } else {
          pdf.setFillColor(220, 220, 220);
          pdf.rect(x, y, tileW, tileH, "F");
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(7);
          pdf.text("Image unavailable", x + 2, y + tileH / 2);
        }
      });

    }
  }

  // ── Last page: Items by Vendor ───────────────────────────────────────
  pdf.addPage();

  // Page header
  pdf.setFillColor(40, 40, 40);
  pdf.rect(0, 0, pageWidth, 14, "F");
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.setTextColor(255, 255, 255);
  pdf.text(`Room ${room.displayName} — Items Required`, marginX, 9);
  pdf.setTextColor(0);

  if (itemsNeeded.length === 0) {
    pdf.setFont("helvetica", "italic");
    pdf.setFontSize(11);
    pdf.text("No items needed for this room.", marginX, 30);
  } else {
    // Group by vendor
    const byVendor = new Map();
    itemsNeeded.forEach((row) => {
      if (!byVendor.has(row.vendorName)) byVendor.set(row.vendorName, []);
      byVendor.get(row.vendorName).push(row);
    });

    // Sort vendors alphabetically; rows within each vendor by unit cost asc
    const vendorsSorted = Array.from(byVendor.entries()).sort(([a], [b]) =>
      a.localeCompare(b)
    );

    const tableBody = [];
    vendorsSorted.forEach(([vendorName, rows]) => {
      rows.sort((a, b) => a.unitCost - b.unitCost);
      const vendorSubtotal = rows.reduce((s, r) => s + r.lineTotal, 0);

      // Vendor header row
      tableBody.push([
        { content: vendorName, styles: { fontStyle: "bold", fillColor: [230, 230, 230] } },
        { content: "", styles: { fillColor: [230, 230, 230] } },
        { content: "", styles: { fillColor: [230, 230, 230] } },
        {
          content: `Subtotal: ${fmtUSD(vendorSubtotal)}`,
          styles: { fontStyle: "bold", fillColor: [230, 230, 230], halign: "right" },
        },
      ]);

      // Item rows
      rows.forEach((r) => {
        tableBody.push([r.itemName, String(r.qty), fmtUSD(r.unitCost), fmtUSD(r.lineTotal)]);
      });
    });

    // Grand total row
    tableBody.push([
      {
        content: "GRAND TOTAL",
        colSpan: 3,
        styles: { fontStyle: "bold", fillColor: [40, 40, 40], textColor: [255, 255, 255] },
      },
      {
        content: fmtUSD(grandTotal),
        styles: { fontStyle: "bold", fillColor: [40, 40, 40], textColor: [255, 255, 255], halign: "right" },
      },
    ]);

    autoTable(pdf, {
      head: [["Item", "Qty", "Unit Cost", "Line Total"]],
      body: tableBody,
      startY: 20,
      styles: { font: "helvetica", fontSize: 9, cellPadding: 2.5, overflow: "linebreak" },
      headStyles: { fillColor: [60, 60, 60], textColor: 255, fontStyle: "bold" },
      columnStyles: {
        0: { cellWidth: "auto" },
        1: { cellWidth: 18, halign: "right" },
        2: { cellWidth: 30, halign: "right" },
        3: { cellWidth: 30, halign: "right" },
      },
      margin: { left: marginX, right: marginX },
      theme: "grid",
    });
  }

  // ── Stamp standardized page numbers on every page ───────────────────
  stampPageNumbers(pdf);

  // ── Save ─────────────────────────────────────────────────────────────
  const blob = pdf.output("blob");
  downloadBlob(
    blob,
    buildReportFilename(`Room_${room.displayName}_Status_Report`, { ext: "pdf" })
  );
};

export default GenerateRoomStatusReport;
